import React, { useContext, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import * as Sentry from '@sentry/react';
import { debounce } from '@mui/material/utils';
import FormikSelect from './FormikSelect';
import { ApiContext, ErrorObject } from '../../context/ApiContext';
import {
  AddressComponents,
  PlacesAutocompleteDetails,
  PlacesAutocompletePrediction,
} from '../../types';
import { API_V1_HOSTNAME_KEY } from '../../localStorageKeys';

const FormikAddressGoogleAutocomplete = ({
  label,
  id,
  name,
  autocompleteField,
  formName,
  required,
  countryISOCode,
  error,
  clearError,
  assignAddressDetails,
  loadingText,
}: {
  label: string;
  id: string;
  name: string;
  autocompleteField: string;
  formName: string;
  required: boolean;
  countryISOCode?: string;
  error?: string;
  clearError?: (field) => void;
  assignAddressDetails: (
    addressDetails: AddressComponents[],
    prediction: PlacesAutocompletePrediction
  ) => void;
  loadingText?: string;
}) => {
  const { apiFetch } = useContext(ApiContext);
  const [autoCompletePlaceLocations, setAutoCompletePlaceLocations] = useState<
    PlacesAutocompletePrediction[]
  >([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState<boolean>(false);

  const { mutate: fetchAutocompletePlaces } = useMutation(
    (value: { streetAddress: string; countryCode?: string }) =>
      apiFetch(
        `${localStorage.getItem(
          API_V1_HOSTNAME_KEY
        )}onboarding/proxy/google-places-autocomplete?input=${value.streetAddress}`,
        {},
        'GET'
      ),
    {
      onSuccess(unFormattedPlaces: {
        Predictions: PlacesAutocompletePrediction[];
        Status: string;
      }) {
        let places = [];
        if (unFormattedPlaces?.Status === 'OK') {
          places = unFormattedPlaces.Predictions.map(
            (prediction: PlacesAutocompletePrediction) => ({
              Description: prediction.Description,
              PlaceId: prediction.PlaceId,
              ...prediction,
            })
          );
        }

        setAutoCompletePlaceLocations(places);
        setIsLoadingLocations(false);
      },
      onError(responseError: any) {
        Sentry.captureException(new Error('Error fetching address autocomplete results'), {
          extra: { error: JSON.stringify(responseError) },
        });
        setIsLoadingLocations(false);
        setAutoCompletePlaceLocations([]);
      },
    }
  );

  const { mutate: fetchPlaceDetails } = useMutation(
    (PlaceId: string) =>
      apiFetch(
        `${localStorage.getItem(
          API_V1_HOSTNAME_KEY
        )}onboarding/proxy/google-places-details?${new URLSearchParams({
          PlaceId,
        })}`
      ),
    {
      onSuccess(placeDetails: { Result: PlacesAutocompleteDetails; Status: string }, PlaceId) {
        if (placeDetails?.Status === 'OK') {
          const addressComponents = placeDetails.Result.AddressComponents;
          const prediction = autoCompletePlaceLocations?.find(
            (location: PlacesAutocompletePrediction) => location?.PlaceId === PlaceId
          );
          assignAddressDetails(addressComponents, prediction);
        }
      },
      onError(responseError: ErrorObject) {
        if (responseError?.code === 401) return;
        Sentry.captureException(new Error('Error fetching address details'), {
          extra: { error: JSON.stringify(responseError) },
        });
      },
    }
  );

  const fetchPossibleLocations = useMemo(
    () =>
      debounce(async (streetAddress: string, countryCode?: string) => {
        fetchAutocompletePlaces({ streetAddress, countryCode });
      }, 1000),
    []
  );

  const fetchLocationDetails = async (desc: string) => {
    const res = autoCompletePlaceLocations.find(({ Description }) => Description === desc);

    if (!res) return;

    fetchPlaceDetails(res.PlaceId);
  };

  return (
    <FormikSelect
      label={label}
      id={id}
      name={name}
      autoComplete={autocompleteField}
      disableFiltering
      freeSolo
      formName={formName}
      required={required}
      error={error}
      clearError={clearError}
      onTextChange={(e: React.FormEvent<HTMLInputElement>) => {
        const fieldText = e && (e.currentTarget || (e.target as HTMLInputElement)).value;
        if (fieldText) {
          setIsLoadingLocations(true);
          fetchPossibleLocations(fieldText, countryISOCode);
        } else {
          setIsLoadingLocations(false);
          setAutoCompletePlaceLocations([]);
        }
      }}
      onSelectionChange={async (e: { value: string; label: string }) => {
        setIsLoadingLocations(false);
        if (e) {
          fetchLocationDetails(e.value);
        }
      }}
      options={autoCompletePlaceLocations.map(({ Description }) => ({
        value: Description,
        label: Description,
      }))}
      isLoading={isLoadingLocations}
      loadingText={loadingText || 'common.loadingAddresses'}
    />
  );
};

export default FormikAddressGoogleAutocomplete;
